import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';

import {
  ADDITIONAL_INFORMATION_STATUS,
  AdditionalInformation,
} from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import {
  RadiosInput,
  RADIOS_INPUT_DIRECTION,
} from '@hivebrite/design-system/Inputs';

import { shouldDisplayError } from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import { validateRadio } from './validate';

const TYPES = ['toggle', 'input'];

const getRadioComponent = ({ type }) => {
  switch (type) {
    // TODO Rebuild RadioToggle inside DS + reuse it here.
    // case 'toggle':
    //   return RadioToggle;
    case 'input':
    default:
      return RadiosInput;
  }
};

const Radio = ({
  mb = 6,
  ariaDescribedBy,
  ariaLabel,
  direction,
  disabled = false,
  id,
  info = undefined,
  infoIcon = undefined,
  inputs,
  label,
  labelMultiline = true,
  name,
  onBlur,
  onChange,
  onFocus,
  tooltip,
  type = 'input',
  validate = {},
  wrapperProps = {},
}) => {
  const formik = useFormikContext();
  const htmlId = id || name;

  const RadioComponent = useMemo(() => getRadioComponent({ type }), [type]);

  const handleValidate = useCallback(
    (value) =>
      validateRadio({
        required: validate.required,
        value,
      }),
    [validate.required],
  );
  const handleChange = useCallback(
    (e) => {
      formik.setFieldValue(name, e);
      if (onChange) {
        onChange(e);
      }
    },
    [formik, onChange, name],
  );
  const handleBlur = useCallback(() => {
    formik.setFieldTouched(name, true);
    if (onBlur) {
      onBlur();
    }
  }, [formik, onBlur, name]);

  return (
    <Box mb={mb} display="block" {...wrapperProps}>
      <Field name={name} validate={handleValidate}>
        {({
          field, // { name, value, onChange, onBlur }
          meta, // { error, touched, value }
          form,
        }) => (
          <RadioComponent
            {...field}
            ariaDescribedBy={ariaDescribedBy}
            ariaLabel={ariaLabel}
            dataTestId="formik-radio"
            direction={direction}
            disabled={disabled}
            error={shouldDisplayError({ meta, form })}
            id={htmlId}
            initialValue={meta.initialValue}
            inputs={inputs}
            label={label}
            labelMultiline={labelMultiline}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={onFocus}
            required={validate.required}
            selectedValue={field.value}
            tooltip={tooltip}
            touched={meta.touched}
          />
        )}
      </Field>
      {info && typeof info === 'string' && (
        <AdditionalInformation
          iconName={infoIcon}
          id={id}
          status={ADDITIONAL_INFORMATION_STATUS.INFO}
        >
          {info}
        </AdditionalInformation>
      )}
    </Box>
  );
};

Radio.propTypes = {
  mb: PropTypes.number,
  name: PropTypes.string.isRequired,
  inputs: PropTypes.array.isRequired,
  ariaDescribedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  direction: PropTypes.oneOf(RADIOS_INPUT_DIRECTION),
  disabled: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.node,
  labelMultiline: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  tooltip: PropTypes.string,
  type: PropTypes.oneOf(TYPES),
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  wrapperProps: PropTypes.object,
};

export default Radio;
